import type { HandwrittenNoteType } from '@mathflat/domain/HandwrittenNote'
import { makeAutoObservable } from 'mobx'
import { isAndroid, isChrome, isIOS, isSafari } from 'react-device-detect'

import { handwrittenNoteStorage } from '../api/handwrittenNoteStorage'
import { IndexedDbService, type StudentWorksheetNoteValue } from './indexedDb.service'

const isApp = (isAndroid || isIOS) && !isSafari && !isChrome

export class WorksheetProblemNoteService {
  private indexedDbService: IndexedDbService
  isShowMobileOrientationGuide = true

  constructor() {
    this.indexedDbService = new IndexedDbService()
    this.isShowMobileOrientationGuide = !isApp

    makeAutoObservable<this, 'indexedDbService'>(this, {
      indexedDbService: false,
    })
  }

  setIsShowMobileOrientationGuide(value: boolean) {
    this.isShowMobileOrientationGuide = value
  }

  async uploadNotes(
    studentWorksheetId: number | undefined,
    worksheetProblemIds: number[] | undefined,
    noteType: Exclude<HandwrittenNoteType, 'SIGNATURE_WORKBOOK_PAGE'>,
  ) {
    if (!studentWorksheetId || !worksheetProblemIds?.length) {
      return
    }
    const noteDataItems = await this.indexedDbService.getStudentWorksheetNotes(
      worksheetProblemIds.map((problemId) => {
        return {
          studentWorksheetId: Number(studentWorksheetId),
          worksheetProblemId: problemId,
        }
      }),
    )
    const noteDataItemsToBeSubmitted: StudentWorksheetNoteValue[] = noteDataItems.map(
      (noteFromWorksheet) => {
        return {
          ...noteFromWorksheet,
          id: noteFromWorksheet.studentWorksheetId,
          subId: noteFromWorksheet.worksheetProblemId,
        }
      },
    )

    if (!noteDataItems.length) {
      return
    }

    const uploadedItems = await handwrittenNoteStorage.uploadStudentWorksheetNotes(
      noteDataItemsToBeSubmitted,
      noteType,
    )

    // 업로드가 실패한 필기는 로컬 백업을 남겨 다음 제출에서 다시 올린다.
    // 획을 다 지운 필기는 업로드 대상이 아니라 응답에 없으므로, 남겨두면 큐에서 영영 안 지워진다
    const uploadedProblemIds = new Set(uploadedItems.map(({ params }) => params.subId))
    const uploadedNoteDataItems = noteDataItems.filter(
      ({ worksheetProblemId, noteData }) =>
        uploadedProblemIds.has(worksheetProblemId) || !noteData.paths.length,
    )

    if (uploadedNoteDataItems.length) {
      await this.indexedDbService.deleteStudentWorksheetNotes(uploadedNoteDataItems)
    }
  }
}
